import { HomeworkReview } from "@/components/homework/HomeworkReview";
import { Skeleton } from "@/components/shared/Skeleton";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { StudentHomeworkByIdDocument } from "@/generated/graphql";
import { useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { StudentBreadcrumb } from "./StudentBreadcrumb";
import { CLASSROOM_ROOT, type ClassroomScope } from "./classroomScope";
import { useStudentContext } from "./useStudentContext";

/** One of a student's assignments, reached from their homework list. */
export function StudentHomeworkDetailPage({
  classroomId,
  studentId,
  homeworkId,
  scope = "teacher",
}: { classroomId: string; studentId: string; homeworkId: string; scope?: ClassroomScope }) {
  const t = useT();
  const root = CLASSROOM_ROOT[scope];
  const { classroomName, student } = useStudentContext(scope, classroomId, studentId);
  const { data, loading, error } = useQuery(StudentHomeworkByIdDocument, {
    variables: { classroomId, studentId, homeworkId },
  });

  const homework = data?.studentHomeworkById ?? null;
  /** The roster already holds the student, so the review panel can name them without a second query. */
  const review = useMemo(() => (homework ? { ...homework, student } : null), [homework, student]);

  return (
    <TeacherPage
      title={null}
      wide
      embedded={scope === "admin"}
      breadcrumb={
        <StudentBreadcrumb
          scope={scope}
          classroomId={classroomId}
          classroomName={classroomName}
          studentId={studentId}
          studentName={student?.name ?? null}
          trail={[
            <Link
              key="homework"
              to={`${root}/$classroomId/students/$studentId/homework`}
              params={{ classroomId, studentId }}
              className="hover:text-app-ink"
            >
              {t["student.homework"]}
            </Link>,
            homework?.title ?? t["homework.fallbackTitle"],
          ]}
        />
      }
    >
      {loading && !review ? (
        <div className="app-card space-y-3 p-4 sm:p-6">
          <Skeleton className="h-7 w-52" />
          <Skeleton className="h-40" />
        </div>
      ) : error || !review ? (
        <p className="app-card body p-4 text-app-overdue sm:p-6">{t["homework.detailLoadError"]}</p>
      ) : (
        <HomeworkReview homework={review} />
      )}
    </TeacherPage>
  );
}
